import { useState, useEffect } from 'react';
import { authService } from '@/lib/auth/authService';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Loader2, Bug, Check, X, Mail } from 'lucide-react';

// Definir tipos para el estado
interface EmailTestState {
  loading: boolean;
  result: {
    success: boolean;
    message: string;
  } | null;
}

export default function DebugPanel() {
  const [isLoading, setIsLoading] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [showResults, setShowResults] = useState(false);
  const [emailTest, setEmailTest] = useState<EmailTestState>({ loading: false, result: null });

  const runConnectionTest = async () => {
    setIsLoading(true);
    try {
      const results = await authService.testSupabaseConnection();
      setTestResults(results);
      setShowResults(true);
      console.log('Resultados de la prueba:', results);
    } catch (error) {
      console.error('Error al ejecutar las pruebas:', error);
      setTestResults({
        success: false,
        details: { error: String(error) }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testEmailService = async () => {
    setEmailTest({ loading: true, result: null });

    try {
      // Esta prueba utiliza el endpoint de reenvío de email de confirmación
      // Solo funciona si hay un email registrado pero no confirmado
      const testEmail = prompt('Introduce un email para probar el servicio (debe existir en Supabase):');

      if (!testEmail) {
        setEmailTest({
          loading: false,
          result: {
            success: false,
            message: 'Email no proporcionado'
          }
        });
        return;
      }

      const result = await authService.resendConfirmationEmail(testEmail);
      setEmailTest({
        loading: false,
        result: {
          success: result.success,
          message: result.message
        }
      });

    } catch (error) {
      console.error('Error en prueba de email:', error);
      setEmailTest({
        loading: false,
        result: {
          success: false,
          message: String(error)
        }
      });
    }
  };

  return (
    <div className="bg-zinc-900/70 border border-white/10 rounded-lg p-4 max-w-2xl mx-auto mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Bug className="w-5 h-5 text-amber-500" />
          Panel de diagnóstico
        </h2>

        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={() => setShowResults(!showResults)}
        >
          {showResults ? 'Ocultar resultados' : 'Mostrar resultados'}
        </Button>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="w-full"
            onClick={runConnectionTest}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Probando...
              </>
            ) : (
              'Probar conexión Supabase'
            )}
          </Button>

          <Button
            variant="outline"
            className="w-full"
            onClick={testEmailService}
            disabled={emailTest.loading}
          >
            {emailTest.loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Probando email...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Probar servicio de email
              </>
            )}
          </Button>
        </div>

        {showResults && testResults && (
          <div className="p-3 bg-zinc-800 rounded border border-zinc-700 text-sm overflow-auto max-h-80">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium">Estado:</span>
              {testResults.success ? (
                <span className="text-green-400 flex items-center gap-1">
                  <Check className="w-4 h-4" /> Conectado correctamente
                </span>
              ) : (
                <span className="text-red-400 flex items-center gap-1">
                  <X className="w-4 h-4" /> Error de conexión
                </span>
              )}
            </div>

            <pre className="text-xs text-zinc-300 whitespace-pre-wrap">
              {JSON.stringify(testResults.details, null, 2)}
            </pre>
          </div>
        )}

        {emailTest.result && (
          <div className="p-3 bg-zinc-800 rounded border border-zinc-700 text-sm overflow-auto max-h-80">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium">Prueba de email:</span>
              {emailTest.result.success ? (
                <span className="text-green-400 flex items-center gap-1">
                  <Check className="w-4 h-4" /> Enviado correctamente
                </span>
              ) : (
                <span className="text-red-400 flex items-center gap-1">
                  <X className="w-4 h-4" /> Error de envío
                </span>
              )}
            </div>

            <p className="text-zinc-300 mb-2">{emailTest.result.message}</p>
          </div>
        )}

        <div className="p-3 bg-amber-900/20 border border-amber-500/20 rounded-lg">
          <p className="text-amber-200 text-xs">
            Este panel es solo para diagnóstico. Si encuentras problemas con la autenticación o el envío de correos, verifica:
            <br/>
            1. La configuración de SMTP en el panel de Supabase
            <br/>
            2. Las plantillas de correo personalizadas
            <br/>
            3. La configuración de dominios permitidos
          </p>
        </div>
      </div>
    </div>
  );
}
